import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  branchTargetApi,
  BranchTarget,
  BranchSummary,
  CreateTargetInput,
  getMonthName,
  getStatusColor,
  getStatusLabel,
  getProgressBarColor,
} from '@/services/branchTargetApi';
import { branchApi } from '@/services/branchApi';
import { useAuthStore } from '@/store/authStore';
import { getActiveCurrency } from '@/utils/currency';
import { UserRole } from '@/types';
import SimpleSelect from '@/components/common/SimpleSelect';
import {
  Target,
  Plus,
  TrendingUp,
  TrendingDown,
  CheckCircle,
  AlertTriangle,
  Calendar,
  Building2,
  Loader2,
  ChevronLeft,
  ChevronRight,
  X,
  Edit2,
  Trash2,
  ClipboardList,
  Clock,
  CheckSquare,
  Lock,
  RefreshCw,
} from 'lucide-react';

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const currentYear = new Date().getFullYear();
const currentMonth = new Date().getMonth() + 1;

export default function BranchTargets() {
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  const isSuperAdmin = user?.role === UserRole.SUPER_ADMIN;

  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [branchFilter, setBranchFilter] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editTarget, setEditTarget] = useState<BranchTarget | null>(null);
  const [page, setPage] = useState(1);

  // Fetch dashboard summary
  const { data: dashboardData, isLoading: dashboardLoading, isFetching: dashboardFetching } = useQuery({
    queryKey: ['branch-targets-dashboard', selectedYear, selectedMonth],
    queryFn: () => branchTargetApi.getDashboardSummary(selectedYear, selectedMonth),
    staleTime: 2 * 60 * 1000,
  });

  // Fetch targets list
  const { data: targetsData, isLoading: targetsLoading, isFetching: targetsFetching } = useQuery({
    queryKey: ['branch-targets', branchFilter, selectedYear, selectedMonth, page],
    queryFn: () =>
      branchTargetApi.getTargets({
        branchId: branchFilter || undefined,
        year: selectedYear,
        month: selectedMonth,
        page,
        limit: 10,
      }),
    staleTime: 2 * 60 * 1000,
  });

  // Fetch branches
  const { data: branches } = useQuery({
    queryKey: ['branches-list'],
    queryFn: () => branchApi.getBranchList(),
    staleTime: 5 * 60 * 1000,
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: branchTargetApi.deleteTarget,
    onSuccess: () => {
      toast.success('Target deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['branch-targets'] });
      queryClient.invalidateQueries({ queryKey: ['branch-targets-dashboard'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete target');
    },
  });

  const handleDelete = (id: string) => {
    if (!isSuperAdmin) {
      toast.error('Only Super Admin can delete targets');
      return;
    }
    if (window.confirm('Are you sure you want to delete this target?')) {
      deleteMutation.mutate(id);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat(getActiveCurrency().locale, {
      style: 'currency',
      currency: getActiveCurrency().code,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusIcon = (percentage: number) => {
    if (percentage >= 100) return <CheckCircle className="w-5 h-5 text-green-600" />;
    if (percentage >= 75) return <TrendingUp className="w-5 h-5 text-yellow-600" />;
    if (percentage >= 50) return <AlertTriangle className="w-5 h-5 text-orange-600" />;
    return <TrendingDown className="w-5 h-5 text-red-600" />;
  };

  return (
    <div className="px-1 sm:px-3 md:px-6 pt-2 pb-4 sm:pb-6 space-y-3 sm:space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2 sm:gap-3">
          <Target className="w-6 h-6 sm:w-8 sm:h-8 text-purple-600" />
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Branch Targets</h1>
            <p className="text-xs sm:text-sm text-gray-500">Set and track service count targets by branch</p>
          </div>
        </div>
        {isSuperAdmin ? (
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Set Target
          </button>
        ) : (
          <div className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-500 rounded-lg">
            <Lock className="w-4 h-4" />
            <span className="text-sm">Super Admin only</span>
          </div>
        )}
      </div>

      {/* Period Selection */}
      <div className="bg-white rounded-lg border p-4">
        <div className="flex items-center gap-2 mb-3">
          <Calendar className="w-4 h-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-700">Select Period</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <SimpleSelect
            value={String(selectedYear)}
            onChange={(v) => setSelectedYear(parseInt(v))}
            options={[currentYear - 1, currentYear, currentYear + 1].map((year) => ({
              value: String(year),
              label: String(year),
            }))}
            aria-label="Year"
            wrapperClassName="w-28"
            className="focus:!ring-purple-500 focus:!border-purple-500"
          />
          <SimpleSelect
            value={String(selectedMonth)}
            onChange={(v) => setSelectedMonth(parseInt(v))}
            options={MONTHS.map((month) => ({ value: String(month.value), label: month.label }))}
            aria-label="Month"
            wrapperClassName="w-36"
            className="focus:!ring-purple-500 focus:!border-purple-500"
          />
          <SimpleSelect
            value={branchFilter}
            onChange={setBranchFilter}
            options={[
              { value: '', label: 'All Branches' },
              ...(branches ?? []).map((branch: any) => ({ value: branch.id, label: branch.name })),
            ]}
            placeholder="All Branches"
            aria-label="Filter by branch"
            wrapperClassName="w-full sm:w-44"
            className="focus:!ring-purple-500 focus:!border-purple-500"
          />
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                setSelectedYear(currentYear);
                setSelectedMonth(currentMonth);
                setBranchFilter('');
              }}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Reset
            </button>
            <button
              onClick={() => {
                queryClient.invalidateQueries({ queryKey: ['branch-targets-dashboard'] });
                queryClient.invalidateQueries({ queryKey: ['branch-targets'] });
              }}
              disabled={dashboardFetching || targetsFetching}
              className="p-2 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors disabled:opacity-50"
              title="Refresh data"
            >
              <RefreshCw className={`h-4 w-4 ${dashboardFetching || targetsFetching ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Dashboard Summary */}
      {dashboardLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        </div>
      ) : dashboardData ? (
        <>
          {/* Service Count Totals Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Monthly Target */}
            <div className="bg-white rounded-lg border p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-gray-600">Monthly Target</h3>
                <ClipboardList className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex items-baseline gap-2 mb-2">
                <span className="text-3xl font-bold text-gray-900">
                  {dashboardData.totals.serviceCountTarget || 0}
                </span>
                <span className="text-sm text-gray-500">Services</span>
              </div>
              <p className="text-sm text-gray-500">Target services for this month</p>
            </div>

            {/* Completed */}
            <div className="bg-green-50 rounded-lg border border-green-200 p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-green-700">Completed</h3>
                <CheckSquare className="w-5 h-5 text-green-600" />
              </div>
              <div className="flex items-baseline gap-2 mb-2">
                <span className="text-3xl font-bold text-green-700">
                  {dashboardData.totals.serviceCountCompleted || 0}
                </span>
                <span className="text-sm text-green-600">Services</span>
              </div>
              <p className="text-sm text-green-600">
                {(dashboardData.totals.serviceCountPercentage || 0).toFixed(1)}% of target achieved
              </p>
            </div>

            {/* Pending */}
            <div className="bg-orange-50 rounded-lg border border-orange-200 p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-orange-700">Pending</h3>
                <Clock className="w-5 h-5 text-orange-600" />
              </div>
              <div className="flex items-baseline gap-2 mb-2">
                <span className="text-3xl font-bold text-orange-700">
                  {dashboardData.totals.serviceCountPending || 0}
                </span>
                <span className="text-sm text-orange-600">Services</span>
              </div>
              <p className="text-sm text-orange-600">Remaining to reach target</p>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Overall Progress</span>
              <span className="text-sm font-medium text-gray-900">
                {(dashboardData.totals.serviceCountPercentage || 0).toFixed(1)}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className={`h-3 rounded-full ${getProgressBarColor(dashboardData.totals.serviceCountPercentage || 0)}`}
                style={{ width: `${Math.min(100, dashboardData.totals.serviceCountPercentage || 0)}%` }}
              />
            </div>
          </div>

          {/* Branch-wise Cards */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Branch Performance - {getMonthName(selectedMonth)} {selectedYear}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {dashboardData.branches.map((branch: BranchSummary) => (
                <div
                  key={branch.branchId}
                  className={`bg-white rounded-lg border p-4 ${!branch.hasTarget ? 'border-dashed border-gray-300' : ''}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="font-medium text-gray-900">{branch.branchName}</h3>
                      <span className="text-xs text-gray-500">{branch.branchCode}</span>
                    </div>
                    {branch.hasTarget ? (
                      <span
                        className={`text-xs px-2 py-1 rounded-full border ${getStatusColor(branch.serviceCountPercentage || 0)}`}
                      >
                        {getStatusLabel(branch.serviceCountPercentage || 0)}
                      </span>
                    ) : (
                      <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-500">No Target</span>
                    )}
                  </div>

                  {branch.hasTarget ? (
                    <div className="space-y-3">
                      {/* Service Count Progress */}
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div className="bg-blue-50 rounded p-2">
                          <p className="text-lg font-bold text-blue-700">{branch.serviceCountTarget || 0}</p>
                          <p className="text-xs text-blue-600">Target</p>
                        </div>
                        <div className="bg-green-50 rounded p-2">
                          <p className="text-lg font-bold text-green-700">{branch.serviceCountCompleted || 0}</p>
                          <p className="text-xs text-green-600">Done</p>
                        </div>
                        <div className="bg-orange-50 rounded p-2">
                          <p className="text-lg font-bold text-orange-700">{branch.serviceCountPending || 0}</p>
                          <p className="text-xs text-orange-600">Pending</p>
                        </div>
                      </div>

                      {/* Progress Bar */}
                      <div>
                        <div className="flex justify-between text-xs text-gray-600 mb-1">
                          <span>Progress</span>
                          <span>{(branch.serviceCountPercentage || 0).toFixed(1)}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full ${getProgressBarColor(branch.serviceCountPercentage || 0)}`}
                            style={{ width: `${Math.min(100, branch.serviceCountPercentage || 0)}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <p className="text-sm text-gray-500 mb-2">No target set for this period</p>
                      {isSuperAdmin && (
                        <button
                          onClick={() => setShowCreateModal(true)}
                          className="text-sm text-purple-600 hover:text-purple-700"
                        >
                          + Set Target
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </>
      ) : null}

      {/* Targets Table */}
      <div className="bg-white rounded-lg border overflow-hidden">
        <div className="px-4 py-3 border-b bg-gray-50">
          <h2 className="font-semibold text-gray-900">All Targets</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Branch</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Period</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Monthly Target</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Completed</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Pending</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Progress</th>
                {isSuperAdmin && (
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Actions</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {targetsLoading ? (
                <tr>
                  <td colSpan={isSuperAdmin ? 7 : 6} className="px-4 py-8 text-center">
                    <Loader2 className="w-6 h-6 animate-spin text-purple-600 mx-auto" />
                  </td>
                </tr>
              ) : targetsData?.targets?.length === 0 ? (
                <tr>
                  <td colSpan={isSuperAdmin ? 7 : 6} className="px-4 py-8 text-center text-gray-500">
                    No targets found for this period
                  </td>
                </tr>
              ) : (
                targetsData?.targets?.map((target: BranchTarget) => (
                  <tr key={target.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-gray-900">{target.branchName}</p>
                        <p className="text-xs text-gray-500">{target.branchCode}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-700">
                        {target.month ? `${getMonthName(target.month)} ${target.year}` : 'Custom Period'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-lg font-bold text-blue-700">{target.serviceCountTarget || 0}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-lg font-bold text-green-700">{target.serviceCountCompleted || 0}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-lg font-bold text-orange-700">{target.serviceCountPending || 0}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-2">
                        <span
                          className={`text-xs px-2 py-1 rounded-full border ${getStatusColor(target.serviceCountPercentage || 0)}`}
                        >
                          {(target.serviceCountPercentage || 0).toFixed(1)}%
                        </span>
                      </div>
                    </td>
                    {isSuperAdmin && (
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => setEditTarget(target)}
                            className="p-1 text-gray-500 hover:text-purple-600 transition-colors"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(target.id)}
                            className="p-1 text-gray-500 hover:text-red-600 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {targetsData?.pagination && targetsData.pagination.totalPages > 1 && (
          <div className="px-4 py-3 border-t flex items-center justify-between">
            <div className="text-sm text-gray-500">
              Page {page} of {targetsData.pagination.totalPages}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-2 rounded-lg border hover:bg-gray-50 disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setPage((p) => Math.min(targetsData.pagination.totalPages, p + 1))}
                disabled={page === targetsData.pagination.totalPages}
                className="p-2 rounded-lg border hover:bg-gray-50 disabled:opacity-50"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || editTarget) && (
        <TargetModal
          target={editTarget}
          branches={branches || []}
          selectedYear={selectedYear}
          selectedMonth={selectedMonth}
          onClose={() => {
            setShowCreateModal(false);
            setEditTarget(null);
          }}
          onSuccess={() => {
            setShowCreateModal(false);
            setEditTarget(null);
            queryClient.invalidateQueries({ queryKey: ['branch-targets'] });
            queryClient.invalidateQueries({ queryKey: ['branch-targets-dashboard'] });
          }}
        />
      )}
    </div>
  );
}

// Target Modal Component
interface TargetModalProps {
  target: BranchTarget | null;
  branches: any[];
  selectedYear: number;
  selectedMonth: number;
  onClose: () => void;
  onSuccess: () => void;
}

function TargetModal({ target, branches, selectedYear, selectedMonth, onClose, onSuccess }: TargetModalProps) {
  const [formData, setFormData] = useState<CreateTargetInput>({
    branchId: target?.branchId || '',
    periodType: 'MONTHLY',
    month: target?.month || selectedMonth,
    year: target?.year || selectedYear,
    serviceCountTarget: target?.serviceCountTarget || 0,
    notes: target?.notes || '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.branchId) {
      toast.error('Please select a branch');
      return;
    }

    if (!formData.serviceCountTarget || formData.serviceCountTarget <= 0) {
      toast.error('Please enter a valid service count target');
      return;
    }

    setIsSubmitting(true);
    try {
      if (target) {
        await branchTargetApi.updateTarget(target.id, {
          serviceCountTarget: formData.serviceCountTarget,
          notes: formData.notes,
        });
        toast.success('Target updated successfully');
      } else {
        await branchTargetApi.createTarget(formData);
        toast.success('Target created successfully');
      }
      onSuccess();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to save target');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-md">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h2 className="text-lg font-semibold text-gray-900">
            {target ? 'Edit Target' : 'Set Branch Target'}
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Branch Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Branch *</label>
            <SimpleSelect
              value={formData.branchId}
              onChange={(v) => setFormData({ ...formData, branchId: v })}
              disabled={!!target}
              options={[
                { value: '', label: 'Select Branch' },
                ...branches.map((branch: any) => ({
                  value: branch.id,
                  label: `${branch.name} (${branch.code})`,
                })),
              ]}
              placeholder="Select Branch"
              aria-label="Branch"
              wrapperClassName="w-full min-w-0"
              className="focus:!ring-purple-500 focus:!border-purple-500"
            />
          </div>

          {/* Period Selection */}
          {!target && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Year *</label>
                <SimpleSelect
                  value={String(formData.year)}
                  onChange={(v) => setFormData({ ...formData, year: parseInt(v) })}
                  options={[currentYear - 1, currentYear, currentYear + 1].map((year) => ({
                    value: String(year),
                    label: String(year),
                  }))}
                  aria-label="Year"
                  wrapperClassName="w-full min-w-0"
                  className="focus:!ring-purple-500 focus:!border-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Month *</label>
                <SimpleSelect
                  value={String(formData.month)}
                  onChange={(v) => setFormData({ ...formData, month: parseInt(v) })}
                  options={MONTHS.map((month) => ({ value: String(month.value), label: month.label }))}
                  aria-label="Month"
                  wrapperClassName="w-full min-w-0"
                  className="focus:!ring-purple-500 focus:!border-purple-500"
                />
              </div>
            </div>
          )}

          {/* Service Count Target */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Service Target *</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                <ClipboardList className="w-4 h-4" />
              </span>
              <input
                type="number"
                min="1"
                step="1"
                value={formData.serviceCountTarget || ''}
                onChange={(e) => setFormData({ ...formData, serviceCountTarget: parseInt(e.target.value) || 0 })}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                placeholder="e.g., 100"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Number of services to complete this month</p>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
            <textarea
              value={formData.notes || ''}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              placeholder="Any additional notes..."
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {target ? 'Update Target' : 'Create Target'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
